// PETRONAS Group (Petroliam Nasional Berhad) — figures keyed to the six public
// results documents the Finance Analyst agent reads (backend: "petronas docs/"):
//
//   FY22        — Quarterly Report Q4 FY2023 (FY2023 vs FY2022)
//   FY23        — Financial Report FY2024, FY2023 column (restated)
//   FY24, FY25  — Financial Report FY2025 (FY2024 column restated where shown)
//   1H25, 1H26  — Interim Financial Report 1H 2026 + 1H 2026 Highlights deck
//
// All amounts are RM million, exactly as printed in the unaudited condensed
// consolidated statements. Nothing here is derived — petronasAnalytics.js owns
// every ratio and roll-up.
//
// Headline revenue and PAT for FY22-FY24 include discontinued operations; FY25
// has none. The continuing-operations lines are kept alongside so like-for-like
// comparisons are possible.

export const COMPANY = {
  name: 'PETRONAS Group',
  legalName: 'Petroliam Nasional Berhad',
  registration: '197401002911 (20076-K)',
  basis: 'Consolidated · MFRS 134 · unaudited condensed',
  units: 'MYR million',
};

export const YEARS = ['FY22', 'FY23', 'FY24', 'FY25'];
export const HALVES = ['1H25', '1H26'];

export const YEAR_META = {
  FY22: { endedOn: '31 Dec 2022', source: 'Quarterly Report Q4 FY2023 (comparative)' },
  FY23: { endedOn: '31 Dec 2023', source: 'Financial Report FY2024 (restated comparative)' },
  FY24: { endedOn: '31 Dec 2024', source: 'Financial Report FY2025 (comparative)' },
  FY25: { endedOn: '31 Dec 2025', source: 'Financial Report FY2025' },
  '1H25': { endedOn: '30 Jun 2025', source: 'Interim Financial Report 1H 2026 (comparative)' },
  '1H26': { endedOn: '30 Jun 2026', source: 'Interim Financial Report 1H 2026' },
};

// ---------- group headline KPIs ----------
// Series ordered [FY22, FY23, FY24, FY25]; halves ordered [1H25, 1H26].

export const GROUP = {
  totalRevenue: [372274, 343597, 319957, 266135],
  continuingRevenue: [330009, 305755, 305131, 266135],
  ebitda: [170746, 128590, 114086, 102998],
  totalPat: [101618, 80714, 55092, 45390],
  patToShareholders: [null, 74361, 49104, 40775],
  cffo: [135286, 114158, 102460, 85163],
  capitalInvestments: [50128, 52765, 54225, 41582],
  ppeCapex: [37843, 47933, 51868, 40119],
  depreciation: [33945, 37787, 38980, 37940],
  dividendsToShareholders: [50000, 40000, 32000, 32000],
  gearingPct: [20.3, 19.8, 19.6, 20.7],
  roacePct: [19.9, 14.4, 9.7, 8.2],
};

export const GROUP_HALF = {
  totalRevenue: [132563, 152441],
  ebitda: [54446, 56790],
  totalPat: [26192, 27224],
  patToShareholders: [23640, 22728],
  cffo: [48092, 47506],
  capitalInvestments: [17737, 41363],
  ppeCapex: [16791, 16806],
  depreciation: [18762, 14917],
  dividendsPaid: [10000, 8000],
};

// Balance-sheet positions as at 30 June 2026 (gearing/ROACE as reported).
export const H1_26_POSITION = { gearingPct: 21.2, roacePct: 8.5 };

// ---------- market backdrop (results presentations) ----------
export const MARKET = {
  FY24: { brent: 80.76, jcc: 83.9, usdMyr: 4.58 },
  FY25: { brent: 69.1, jcc: 74.28, usdMyr: 4.28 },
  '1H25': { brent: 71.87, jcc: 76.89, usdMyr: 4.38 },
  '1H26': { brent: 92.31, jcc: 87.3, usdMyr: 3.98 },
};

// FY2025 deck, page 5: PETRONAS PAT margin against its peer group.
export const PEER_PAT_MARGIN = { years: ['2023', '2024', '2025'], petronas: [23, 17, 17] };

// FY2025 deck, page 8: contributions to the Government, RM billion.
export const GOVT_CONTRIBUTIONS_FY25 = [
  { label: 'Dividend to Government', value: 32.0 },
  { label: 'Taxes', value: 19.6 },
  { label: 'Petroleum cash payments', value: 10.8 },
  { label: 'State sales tax', value: 4.2 },
  { label: 'Export duty', value: 0.5 },
  { label: 'National Trust Fund', value: 0.5 },
];

// ---------- balance sheet ----------
// Columns: [FY22, FY23, FY24, FY25, 1H26]
export const BS_PERIODS = ['FY22', 'FY23', 'FY24', 'FY25', '1H26'];

export const BALANCE_SHEET = {
  ppe: [301218, 326398, 327356, 283923, 279915],
  investmentProperties: [12658, 12275, 16716, 18939, 18753],
  associatesJvs: [8114, 10368, 12219, 11558, 43367],
  intangibles: [26260, 31902, 31175, 30332, 31897],
  longTermReceivables: [41127, 47939, 46690, 49111, 52708],
  ncFundInvestments: [1425, 10778, 16633, 18698, 18034],
  deferredTaxAssets: [20675, 27853, 25459, 21661, 22665],
  totalNonCurrentAssets: [411477, 467513, 476248, 434222, 467339],
  inventories: [15612, 14307, 14096, 12805, 16663],
  receivables: [53998, 57028, 71748, 74235, 84106],
  cFundInvestments: [10945, 11620, 15698, 19046, 22553],
  cash: [201220, 208492, 188476, 204375, 193635],
  heldForSale: [17318, 14341, 407, 30270, 9994],
  totalCurrentAssets: [299093, 305788, 290425, 340731, 326951],
  totalAssets: [710570, 773301, 766673, 774953, 794290],
  shareCapital: [100, 100, 100, 100, 100],
  reserves: [401509, 443369, 451115, 448208, 449047],
  shareholdersEquity: [401609, 443469, 451215, 448308, 449147],
  nci: [58822, 59396, 55395, 53777, 55226],
  totalEquity: [460431, 502865, 506610, 502085, 504373],
  ncBorrowings: [96345, 98754, 90837, 108137, 102847],
  deferredTaxLiabilities: [11829, 13297, 13029, 9527, 8903],
  otherLongTermLiabilities: [50418, 64434, 64766, 63122, 60847],
  totalNonCurrentLiabilities: [158592, 176485, 168632, 180786, 172597],
  payables: [63677, 68076, 67156, 65204, 64989],
  cBorrowings: [7812, 12867, 20060, 13466, 23934],
  taxation: [8438, 3931, 4064, 2300, 10140],
  dividendPayable: [0, 0, 0, 0, 12000],
  heldForSaleLiabilities: [11620, 9077, 151, 11112, 6257],
  totalCurrentLiabilities: [91547, 93951, 91431, 92082, 117320],
  totalLiabilities: [250139, 270436, 260063, 272868, 289917],
};

// ---------- statement of profit or loss (continuing operations) ----------
// Columns: [FY22, FY23, FY24, FY25, 1H25, 1H26]
export const PL_PERIODS = ['FY22', 'FY23', 'FY24', 'FY25', '1H25', '1H26'];

export const PROFIT_AND_LOSS = {
  revenue: [330009, 305755, 305131, 266135, 132563, 152441],
  costOfRevenue: [-175509, -182465, -187892, -162516, -78768, -81453],
  grossProfit: [154500, 123290, 117239, 103619, 53795, 70988],
  sellingDistribution: [-7324, -8943, -9950, -8651, -4211, -4395],
  administration: [-13888, -15245, -17993, -16507, -8234, -7179],
  impairments: [-1749, -6096, -4882, -2618, -468, -813],
  otherExpenses: [-3312, -3359, -3000, -8603, -2798, -769],
  otherIncome: [7723, 8479, 6016, 7406, 2814, 10472],
  operatingProfit: [135950, 98126, 87430, 74646, 40898, 68304],
  financingCosts: [-4929, -5500, -5878, -7350, -3233, -3700],
  shareOfAssociatesJvs: [957, 872, 581, 195, 170, -21637],
  profitBeforeTax: [131978, 93498, 82133, 67491, 37835, 42967],
  taxExpense: [-34173, -14559, -26348, -22101, -11643, -15743],
  patContinuing: [97805, 78939, 55785, 45390, 26192, 27224],
  patDiscontinued: [3813, 1775, -693, 0, 0, 0],
  totalPat: [101618, 80714, 55092, 45390, 26192, 27224],
};

// ---------- cash flow ----------
// Columns: [FY22, FY23, FY24, FY25, 1H25, 1H26]
export const CASH_FLOW = {
  operating: [135286, 114158, 102460, 85163, 48092, 47506],
  investing: [-39431, -59296, -71010, -32036, -16230, -48762],
  financing: [-69884, -53098, -46365, -23432, 2178, -7701],
  dividendsPaid: [-50000, -40000, -32000, -32000, -10000, -8000],
  dividendsToNci: [-6039, -7262, -5927, -4925, -2628, -3725],
  drawdowns: [25733, 16570, 19766, 58968, 34849, 22331],
  repayments: [-34934, -15441, -20077, -44737, -19339, -17898],
};

// ---------- segments ----------
// FY23-FY25 on the current four-segment basis (FY23 restated in the FY2024
// report). FY22 used an older "Gas" segment and is deliberately left out.
export const SEGMENT_YEARS = ['FY23', 'FY24', 'FY25'];
export const SEGMENT_NAMES = ['Upstream', 'Gas & Maritime', 'Downstream', 'Corporate & Others'];

export const SEGMENTS = {
  Upstream: {
    revenue: [141846, 139958, 111866],
    pat: [39578, 34898, 26211],
    capex: [27105, 27979, 19147],
    half: { revenue: [57610, 61555], pat: [16549, 28113], capex: [8565, 8990] },
  },
  'Gas & Maritime': {
    revenue: [127441, 131093, 119889],
    pat: [31859, 19887, 20861],
    capex: [11873, 11750, 12288],
    half: { revenue: [59991, 68124], pat: [10409, 16694], capex: [5357, 5353] },
  },
  Downstream: {
    revenue: [185414, 153945, 119963],
    pat: [7097, -722, -1884],
    capex: [5753, 4680, 4466],
    half: { revenue: [58145, 74287], pat: [-865, -15157], capex: [1562, 25987] },
  },
  'Corporate & Others': {
    revenue: [14336, 14008, 13168],
    pat: [2289, -773, -460],
    capex: [8034, 9816, 5681],
    half: { revenue: [6537, 5567], pat: [-622, -1119], capex: [2253, 1033] },
  },
};

// ---------- operations ----------
// Columns: [FY22, FY23, FY24, FY25, 1H25, 1H26]
export const OPS_PERIODS = ['FY22', 'FY23', 'FY24', 'FY25', '1H25', '1H26'];

export const OPERATIONS = {
  productionCrude: [859, 850, 813, 779, 792, 657], // '000 boe/d
  productionGas: [1575, 1581, 1638, 1644, 1611, 1677],
  productionTotal: [2434, 2431, 2451, 2423, 2403, 2334],
  entitlementTotal: [1678, 1663, 1671, 1659, 1654, 1605],
  lngSalesMt: [34.23, 32.9, 35.65, 36.62, 17.34, 20.29],
  salesGasMmscfd: [2938, 2878, 3050, 2922, 2769, 3111],
  chemicalsMt: [8.3, 9.4, 10.1, 10.5, 5.1, 5.0],
};

// The one-off items that make 1H 2026 PAT hard to read at face value.
export const H1_26_ONE_OFFS = {
  prefchemLossRecognised: 14800, // non-cash, inside share of loss of JVs
  downstreamPatExOneOff: 7000,
};
